import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { httpsCallable } from 'firebase/functions';
import * as React from 'react';
import { DayliteContact } from 'shared/types/dayliteModels';
import { functions } from '../../firebase/firebaseConfig';

export default function DeveloperPage() {
  const [response, setResponse] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Daylite token test states
  const [tokenResponse, setTokenResponse] = React.useState<{
    success: boolean;
    isValid?: boolean;
    token?: string;
    message?: string;
  } | null>(null);
  const [tokenLoading, setTokenLoading] = React.useState(false);
  const [tokenError, setTokenError] = React.useState<string | null>(null);

  // Events query states
  const [eventsData, setEventsData] = React.useState<{
    results?: any[];
    next?: string;
  } | null>(null);
  const [eventsLoading, setEventsLoading] = React.useState(false);
  const [eventsError, setEventsError] = React.useState<string | null>(null);

  // Contacts query states - updated type to DayliteContact
  const [contactsData, setContactsData] = React.useState<DayliteContact | null>(null);
  const [contactsLoading, setContactsLoading] = React.useState(false);
  const [contactsError, setContactsError] = React.useState<string | null>(null);

  // Email test states
  const [emailResult, setEmailResult] = React.useState<any>(null);
  const [emailLoading, setEmailLoading] = React.useState(false);
  const [emailError, setEmailError] = React.useState<string | null>(null);

  // Day-of message test states
  const [dayOfResult, setDayOfResult] = React.useState<any>(null);
  const [dayOfLoading, setDayOfLoading] = React.useState(false);
  const [dayOfError, setDayOfError] = React.useState<string | null>(null);

  // Client form reminders test states
  const [reminderDate, setReminderDate] = React.useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [reminderResult, setReminderResult] = React.useState<any>(null);
  const [reminderLoading, setReminderLoading] = React.useState(false);
  const [reminderError, setReminderError] = React.useState<string | null>(null);

  const callHelloWorld = async () => {
    setLoading(true);
    setError(null);
    try {
      const helloWorld = httpsCallable(functions, 'helloWorld');
      const result = await helloWorld();
      setResponse((result.data as { message: string }).message);
    } catch (err) {
      console.error('Error calling Firebase function:', err);
      setError('Failed to call the function. See console for details.');
    } finally {
      setLoading(false);
    }
  };

  const testDayliteToken = async () => {
    setTokenLoading(true);
    setTokenError(null);
    setTokenResponse(null);

    try {
      const testTokenFunction = httpsCallable(functions, 'testDayliteToken');
      const result = await testTokenFunction();
      setTokenResponse(
        result.data as {
          success: boolean;
          isValid?: boolean;
          token?: string;
          message?: string;
        }
      );
    } catch (err) {
      console.error('Error testing Daylite token:', err);
      setTokenError('Failed to test Daylite token. See console for details.');
    } finally {
      setTokenLoading(false);
    }
  };

  const testContactsQuery = async () => {
    setContactsLoading(true);
    setContactsError(null);
    setContactsData(null);

    try {
      const fetchContactFunction = httpsCallable(functions, 'fetchContactByEmail');
      const result = await fetchContactFunction({
        email: 'office@jarrellentertainment.com'
      });

      const data = result.data as {
        success: boolean;
        data?: DayliteContact;
        message?: string;
      };

      if (data.success && data.data) {
        setContactsData(data.data);
      } else {
        setContactsError(data.message || 'Unknown error occurred');
      }
    } catch (err) {
      console.error('Error fetching contact by email:', err);
      setContactsError('Failed to fetch contact by email. See console for details.');
    } finally {
      setContactsLoading(false);
    }
  };

  // Test send email function
  const testSendEmail = async () => {
    setEmailLoading(true);
    setEmailError(null);
    setEmailResult(null);

    try {
      const sendEmailFunction = httpsCallable(functions, 'sendEmailsByIds');
      const result = await sendEmailFunction({
        contactIds: ['6068'], // Test with the specified ID
        emailOptions: {
          template: {
            subject: 'Test Email from Developer Console',
            text: 'This is a test email sent from the developer console.',
            html: '<p>This is a <strong>test email</strong> sent from the developer console.</p>'
          },
          from: 'noreply@jarrellentertainment.com',
          replyTo: 'office@jarrellentertainment.com'
        }
      });

      const data = result.data as any;
      setEmailResult(data);
    } catch (err) {
      console.error('Error sending test email:', err);
      setEmailError('Failed to send test email. See console for details.');
    } finally {
      setEmailLoading(false);
    }
  };

  // Test day-of message function
  const testDayOfMessage = async () => {
    setDayOfLoading(true);
    setDayOfError(null);
    setDayOfResult(null);

    try {
      const sendDayOfFunction = httpsCallable(functions, 'sendDayOfMessages');
      const result = await sendDayOfFunction({
        testDate: '2030-01-01'
      });

      const data = result.data as any;
      setDayOfResult(data);
    } catch (err) {
      console.error('Error sending day-of messages:', err);
      setDayOfError('Failed to send day-of messages. See console for details.');
    } finally {
      setDayOfLoading(false);
    }
  };

  // Test client form reminders function
  const testClientFormReminders = async () => {
    setReminderLoading(true);
    setReminderError(null);
    setReminderResult(null);

    try {
      const remindersFunction = httpsCallable(functions, 'sendClientFormReminders');
      const result = await remindersFunction({
        testDate: reminderDate
      });

      const data = result.data as any;
      setReminderResult(data);
    } catch (err) {
      console.error('Error testing client form reminders:', err);
      setReminderError('Failed to test client form reminders. See console for details.');
    } finally {
      setReminderLoading(false);
    }
  };

  // Helper to display nested JSON data
  const JsonDisplay = ({ data }: { data: any }) => {
    if (!data) return null;

    if (Array.isArray(data)) {
      return (
        <Box sx={{ pl: 2 }}>
          {data.map((item, i) => (
            <Box key={i} sx={{ mb: 1 }}>
              <Typography variant="caption" component="div" sx={{ fontWeight: 'bold' }}>
                Item {i + 1}:
              </Typography>
              <JsonDisplay data={item} />
            </Box>
          ))}
        </Box>
      );
    }

    if (typeof data === 'object') {
      return (
        <Box sx={{ pl: 2 }}>
          {Object.entries(data).map(([key, value]) => (
            <Box key={key} sx={{ mb: 1 }}>
              <Typography variant="caption" component="div" sx={{ fontWeight: 'bold' }}>
                {key}:
              </Typography>
              {typeof value === 'object' ? (
                <JsonDisplay data={value} />
              ) : (
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {value?.toString() || '(empty)'}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      );
    }

    return (
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        {data?.toString() || '(empty)'}
      </Typography>
    );
  };

  return (
    <Box>
      <Typography variant="h3" gutterBottom>
        Dev
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3
        }}
      >
        <Button variant="contained" color="primary" onClick={callHelloWorld} disabled={loading}>
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Call Hello World Function'}
        </Button>

        <Button variant="contained" color="secondary" onClick={testDayliteToken} disabled={tokenLoading}>
          {tokenLoading ? <CircularProgress size={24} color="inherit" /> : 'Test Daylite Token'}
        </Button>

        <Button variant="contained" color="info" onClick={testContactsQuery} disabled={contactsLoading}>
          {contactsLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Fetch Contact: office@jarrellentertainment.com'
          )}
        </Button>

        <Button variant="contained" color="success" onClick={testSendEmail} disabled={emailLoading}>
          {emailLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Test Send Email (ID: 6068)'
          )}
        </Button>

        <Button variant="contained" color="warning" onClick={testDayOfMessage} disabled={dayOfLoading}>
          {dayOfLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Test Day-of Message (2030-01-01)'
          )}
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            type="date"
            size="small"
            label="Reminder Date"
            value={reminderDate}
            onChange={(e) => setReminderDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 170 }}
          />
          <Button variant="contained" color="error" onClick={testClientFormReminders} disabled={reminderLoading}>
            {reminderLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Test Client Form Reminders'
            )}
          </Button>
        </Box>
      </Box>

      {response && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography>
              <strong>Response:</strong> {response}
            </Typography>
          </CardContent>
        </Card>
      )}

      {error && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography color="error">{error}</Typography>
          </CardContent>
        </Card>
      )}

      {tokenResponse && (
        <Card sx={{ mt: 3 }}>
          <CardHeader title="Daylite Token Test" sx={{ pb: 0 }} />
          <CardContent>
            <Typography>
              <strong>Success:</strong> {tokenResponse.success ? 'Yes' : 'No'}
            </Typography>
            {tokenResponse.isValid !== undefined && (
              <Typography>
                <strong>Token Valid:</strong> {tokenResponse.isValid ? 'Yes' : 'No'}
              </Typography>
            )}
            {tokenResponse.token && (
              <Typography>
                <strong>Token Fragment:</strong> {tokenResponse.token}
              </Typography>
            )}
            {tokenResponse.message && (
              <Typography>
                <strong>Message:</strong> {tokenResponse.message}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {tokenError && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography color="error">{tokenError}</Typography>
          </CardContent>
        </Card>
      )}

      {eventsData?.results && (
        <Card sx={{ mt: 3 }}>
          <CardHeader
            title="Daylite Events (Opportunities)"
            subheader={`Found ${eventsData.results?.length || 0} events`}
            sx={{ pb: 0 }}
          />
          <CardContent>
            {eventsData.results?.length > 0 ? (
              <Box
                sx={{
                  maxHeight: '400px',
                  overflow: 'auto'
                }}
              >
                {eventsData.results.map((event, index) => (
                  <Card key={index} variant="outlined" sx={{ mb: 2, p: 1 }}>
                    <Typography variant="subtitle1">{event.name || 'Unnamed Opportunity'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID: {event.self?.split('/').pop() || 'Unknown'}
                    </Typography>
                    {event.value && <Typography variant="body2">Value: {event.value}</Typography>}
                  </Card>
                ))}
              </Box>
            ) : (
              <Typography>No events found</Typography>
            )}

            {eventsData.next && (
              <Typography variant="caption" sx={{ display: 'block', mt: 2 }}>
                More results available. Next page cursor: {eventsData.next.split('?').pop()}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {eventsError && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography color="error">{eventsError}</Typography>
          </CardContent>
        </Card>
      )}

      {/* Updated contact data display to show stringified JSON */}
      {contactsData && (
        <Card sx={{ mt: 3 }}>
          <CardHeader
            title="Contact Details"
            subheader={`${contactsData.first_name || ''} ${contactsData.last_name || ''}`}
            sx={{ pb: 0 }}
          />
          <CardContent>
            <Box
              sx={{
                maxHeight: '600px',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                p: 2,
                borderRadius: 1
              }}
            >
              {JSON.stringify(contactsData, null, 2)}
            </Box>
          </CardContent>
        </Card>
      )}

      {contactsError && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography color="error">{contactsError}</Typography>
          </CardContent>
        </Card>
      )}

      {emailResult && (
        <Card sx={{ mt: 3 }}>
          <CardHeader title="Email Test Results" sx={{ pb: 0 }} />
          <CardContent>
            <Box
              sx={{
                maxHeight: '600px',
                overflow: 'auto'
              }}
            >
              <Typography>
                <strong>Success:</strong> {emailResult.success ? 'Yes' : 'No'}
              </Typography>
              {emailResult.data && (
                <>
                  <Typography sx={{ mt: 1 }}>
                    <strong>Emails Sent:</strong> {emailResult.data.sentTo?.length || 0}
                  </Typography>
                  <Typography>
                    <strong>Failed Contacts:</strong> {emailResult.data.failed?.length || 0}
                  </Typography>
                  {emailResult.data.emailIds && (
                    <Typography>
                      <strong>Email IDs Created:</strong> {emailResult.data.emailIds.length}
                    </Typography>
                  )}
                  <JsonDisplay data={emailResult.data} />
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {emailError && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography color="error">{emailError}</Typography>
          </CardContent>
        </Card>
      )}

      {dayOfResult && (
        <Card sx={{ mt: 3 }}>
          <CardHeader title="Day-of Message Test Results" sx={{ pb: 0 }} />
          <CardContent>
            <Box
              sx={{
                maxHeight: '600px',
                overflow: 'auto'
              }}
            >
              <Typography>
                <strong>Success:</strong> {dayOfResult.success ? 'Yes' : 'No'}
              </Typography>
              <Typography>
                <strong>Date:</strong> {dayOfResult.date}
              </Typography>
              <Typography>
                <strong>Enabled:</strong> {dayOfResult.enabled ? 'Yes' : 'No (Dry Run)'}
              </Typography>
              {dayOfResult.stats && (
                <>
                  <Typography sx={{ mt: 1 }}>
                    <strong>Total Recipients:</strong> {dayOfResult.stats.total}
                  </Typography>
                  <Typography>
                    <strong>Messages Sent:</strong> {dayOfResult.stats.sent}
                  </Typography>
                  <Typography>
                    <strong>Not Opted In:</strong> {dayOfResult.stats.notOptedIn}
                  </Typography>
                  <Typography>
                    <strong>Errors:</strong> {dayOfResult.stats.errors}
                  </Typography>
                </>
              )}
              <Typography sx={{ mt: 2 }}>
                <strong>Message:</strong> {dayOfResult.message}
              </Typography>
              {dayOfResult.recipients && dayOfResult.recipients.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Recipients:
                  </Typography>
                  <JsonDisplay data={dayOfResult.recipients} />
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {dayOfError && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography color="error">{dayOfError}</Typography>
          </CardContent>
        </Card>
      )}

      {reminderResult && (
        <Card sx={{ mt: 3 }}>
          <CardHeader title="Client Form Reminders Test Results" sx={{ pb: 0 }} />
          <CardContent>
            <Box
              sx={{
                maxHeight: '600px',
                overflow: 'auto'
              }}
            >
              <Typography>
                <strong>Success:</strong> {reminderResult.success ? 'Yes' : 'No'}
              </Typography>
              <Typography>
                <strong>Date:</strong> {reminderResult.date}
              </Typography>
              <Typography>
                <strong>Message:</strong> {reminderResult.message}
              </Typography>
              {reminderResult.config && (
                <Typography>
                  <strong>Frequency:</strong> every {reminderResult.config.clientReminderFrequencyDays} days
                </Typography>
              )}
              {reminderResult.stats && (
                <>
                  <Typography sx={{ mt: 1 }}>
                    <strong>Total Forms Checked:</strong> {reminderResult.stats.total}
                  </Typography>
                  <Typography>
                    <strong>Processed:</strong> {reminderResult.stats.processed}
                  </Typography>
                  <Typography>
                    <strong>Reminders Sent:</strong> {reminderResult.stats.remindersSent}
                  </Typography>
                  <Typography>
                    <strong>Skipped:</strong> {reminderResult.stats.skipped}
                  </Typography>
                  <Typography>
                    <strong>Errors:</strong> {reminderResult.stats.errors}
                  </Typography>
                  <Typography>
                    <strong>Elapsed Time:</strong> {reminderResult.stats.elapsedSeconds}s
                  </Typography>
                  {reminderResult.stats.timeoutReached && (
                    <Typography color="warning.main" sx={{ mt: 1 }}>
                      ⚠️ <strong>Timeout Warning:</strong> Processing stopped early
                    </Typography>
                  )}
                </>
              )}
              {reminderResult.results && reminderResult.results.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Detailed Results:
                  </Typography>
                  <JsonDisplay data={reminderResult.results} />
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {reminderError && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography color="error">{reminderError}</Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
